/**
 * GFG-194 (Send Queue, GFG Admin) — unit U6. One function per `/admin/campaigns/send-queue` endpoint.
 * Mirrors apps/gfgf-ui/src/api/adminContacts.ts: query params are snake_case, arrays are
 * comma-joined, empty/absent values are omitted from the querystring; JSON response fields are
 * camelCase. Wire shapes below match the live backend
 * (apps/gfgf-api/src/controllers/campaigns/sendQueueController.ts) exactly.
 */
import type {
  RescheduleSendBody,
  SendQueueContact,
  SendQueueContactEmailPreview,
  SendQueueContactsResult,
  SendQueueEmailPreview,
  SendQueueFilterOptions,
  SendQueueItem,
  SendQueueListParams,
  SendQueueListResult,
  UpdateSendContactEmailBody,
} from "@/interfaces/sendQueueCampaigns";
import { apiClient } from "@/lib/apiClient";

/** GET /admin/campaigns/send-queue -- paginated, filterable, sortable list of upcoming/queued sends. */
export async function fetchSendQueue(params?: SendQueueListParams): Promise<SendQueueListResult> {
  const query: Record<string, string | number> = {};
  if (params?.window) query.window = params.window;
  if (params?.search) query.search = params.search;
  if (params?.campaign_type?.length) query.campaign_type = params.campaign_type.join(",");
  if (params?.send_type?.length) query.send_type = params.send_type.join(",");
  if (params?.status?.length) query.status = params.status.join(",");
  if (params?.account_lead_id?.length) query.account_lead_id = params.account_lead_id.join(",");
  if (params?.sortBy) query.sortBy = params.sortBy;
  if (params?.sortDir) query.sortDir = params.sortDir;
  if (params?.page && params.page > 1) query.page = params.page;
  if (params?.limit) query.limit = params.limit;

  const { data } = await apiClient.get<SendQueueListResult>("/admin/campaigns/send-queue", {
    params: query,
  });
  return data;
}

/** GET /admin/campaigns/send-queue/filter-options -- the account-lead filter dropdown's values. */
export async function fetchSendQueueFilterOptions(): Promise<SendQueueFilterOptions> {
  const { data } = await apiClient.get<{ success: boolean; data: SendQueueFilterOptions }>(
    "/admin/campaigns/send-queue/filter-options",
  );
  return data.data;
}

/** GET /admin/campaigns/send-queue/:id/email -- the rendered email preview for one queued send. */
export async function fetchSendQueueEmail(id: number): Promise<SendQueueEmailPreview> {
  const { data } = await apiClient.get<{ success: boolean; data: SendQueueEmailPreview }>(
    `/admin/campaigns/send-queue/${id}/email`,
  );
  return data.data;
}

/** GET /admin/campaigns/send-queue/:id/contacts -- the recipients a queued send would go to. */
export async function fetchSendQueueContacts(id: number): Promise<SendQueueContactsResult> {
  const { data } = await apiClient.get<SendQueueContactsResult>(
    `/admin/campaigns/send-queue/${id}/contacts`,
  );
  return data;
}

/** POST /admin/campaigns/send-queue/:id/pause */
export async function pauseSend(id: number): Promise<SendQueueItem> {
  const { data } = await apiClient.post<{ success: boolean; data: SendQueueItem }>(
    `/admin/campaigns/send-queue/${id}/pause`,
  );
  return data.data;
}

/** POST /admin/campaigns/send-queue/:id/resume -- status returns to Live (Automated) or Scheduled otherwise. */
export async function resumeSend(id: number): Promise<SendQueueItem> {
  const { data } = await apiClient.post<{ success: boolean; data: SendQueueItem }>(
    `/admin/campaigns/send-queue/${id}/resume`,
  );
  return data.data;
}

/** POST /admin/campaigns/send-queue/:id/send-now -- status becomes "Sending", scheduledAt moves to now. */
export async function sendNow(id: number): Promise<SendQueueItem> {
  const { data } = await apiClient.post<{ success: boolean; data: SendQueueItem }>(
    `/admin/campaigns/send-queue/${id}/send-now`,
  );
  return data.data;
}

/** POST /admin/campaigns/send-queue/:id/reschedule { scheduledAt } */
export async function rescheduleSend(id: number, body: RescheduleSendBody): Promise<SendQueueItem> {
  const { data } = await apiClient.post<{ success: boolean; data: SendQueueItem }>(
    `/admin/campaigns/send-queue/${id}/reschedule`,
    body,
  );
  return data.data;
}

/** POST /admin/campaigns/send-queue/:id/cancel -- the item is removed from the queue. */
export async function cancelSend(id: number): Promise<{ id: number }> {
  const { data } = await apiClient.post<{ success: boolean; data: { id: number } }>(
    `/admin/campaigns/send-queue/${id}/cancel`,
  );
  return data.data;
}

/** GET /admin/campaigns/send-queue/:id/contacts/:contactId/email -- the personalised preview for one recipient. */
export async function fetchSendQueueContactEmail(
  id: number,
  contactId: number,
): Promise<SendQueueContactEmailPreview> {
  const { data } = await apiClient.get<{ success: boolean; data: SendQueueContactEmailPreview }>(
    `/admin/campaigns/send-queue/${id}/contacts/${contactId}/email`,
  );
  return data.data;
}

/** POST /admin/campaigns/send-queue/:id/contacts/:contactId/resend -- re-sends this recipient's gift email. */
export async function resendSendContactEmail(
  id: number,
  contactId: number,
): Promise<SendQueueContact> {
  const { data } = await apiClient.post<{ success: boolean; data: SendQueueContact }>(
    `/admin/campaigns/send-queue/${id}/contacts/${contactId}/resend`,
  );
  return data.data;
}

/** PATCH /admin/campaigns/send-queue/:id/contacts/:contactId { email } -- corrects a bounced recipient's address. */
export async function updateSendContactEmail(
  id: number,
  contactId: number,
  body: UpdateSendContactEmailBody,
): Promise<SendQueueContact> {
  const { data } = await apiClient.patch<{ success: boolean; data: SendQueueContact }>(
    `/admin/campaigns/send-queue/${id}/contacts/${contactId}`,
    body,
  );
  return data.data;
}

/** DELETE /admin/campaigns/send-queue/:id/contacts/:contactId -- drops a recipient from this send. */
export async function removeSendContact(id: number, contactId: number): Promise<{ id: number }> {
  const { data } = await apiClient.delete<{ success: boolean; data: { id: number } }>(
    `/admin/campaigns/send-queue/${id}/contacts/${contactId}`,
  );
  return data.data;
}
